import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Search, Check, RefreshCw, ChevronDown, X } from 'lucide-react';
import { Screen } from '../../../shared/types';
import { Categoria } from '../services/priceService';
import { TelaBannerOtimizada } from '../../banners/services/bannerService';

type SortOption = 'name-asc' | 'name-desc' | 'price-asc' | 'price-desc';

// Tipos de unidade disponíveis
const TIPOS_UNIDADE = [
    { sigla: 'UN', nome: 'UNIDADE' },
    { sigla: 'KG', nome: 'QUILOGRAMA' },
    { sigla: 'MT', nome: 'METRO' },
    { sigla: 'M2', nome: 'METRO QUADRADO' },
    { sigla: 'M3', nome: 'METRO CUBICO' },
    { sigla: 'LT', nome: 'LITRO' },
    { sigla: 'ML', nome: 'MILILITRO' },
    { sigla: 'SC', nome: 'SACA' },
    { sigla: 'FD', nome: 'FARDO' }
] as const;

interface PromotionFiltersProps {
    searchTerm: string;
    onSearchChange: (value: string) => void;
    onSearchClick?: () => void;
    barcode: string;
    onBarcodeChange: (value: string) => void;
    internalCode: string;
    onInternalCodeChange: (value: string) => void;
    selectedCategory: 'all' | number;
    onCategoryChange: (value: 'all' | number) => void;
    promotionKindFilter: 'all' | 'product' | 'video';
    onPromotionKindChange: (value: 'all' | 'product' | 'video') => void;
    promotionFilter: boolean;
    onPromotionFilterChange: (value: boolean) => void;
    screenFilter: 'all' | 'linked' | 'unlinked';
    onScreenFilterChange: (value: 'all' | 'linked' | 'unlinked') => void;
    selectedScreenFilter: number | 'all';
    onSelectedScreenChange: (value: number | 'all') => void;
    sortOption: SortOption;
    onSortChange: (value: SortOption) => void;
    screens: Screen[];
    allSelected: boolean;
    onToggleAll: () => void;
    categorias: Categoria[];
    loadingProdutos?: boolean;
    loadingLinked?: boolean; // Novo: indica se está verificando vinculação de produtos
    optimizedScreens?: TelaBannerOtimizada[];
    selectedUnitTypes: string[];
    onUnitTypeChange: (values: string[]) => void;
}

export const PromotionFilters: React.FC<PromotionFiltersProps> = ({
    searchTerm,
    onSearchChange,
    onSearchClick,
    barcode,
    onBarcodeChange,
    internalCode,
    onInternalCodeChange,
    selectedCategory,
    onCategoryChange,
    promotionKindFilter,
    onPromotionKindChange,
    promotionFilter,
    onPromotionFilterChange,
    screenFilter,
    onScreenFilterChange,
    selectedScreenFilter,
    onSelectedScreenChange,
    sortOption,
    onSortChange,
    screens,
    allSelected,
    onToggleAll,
    categorias,
    loadingProdutos = false,
    loadingLinked = false,
    optimizedScreens = [],
    selectedUnitTypes,
    onUnitTypeChange
}) => {
    // Desabilitar filtros se estiver carregando produtos OU verificando vinculação
    const isDisabled = loadingProdutos || loadingLinked;
    return (
        <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm mb-4 sm:mb-6">
            {/* Primeira linha: Campos de busca + Botão de buscar + Checkbox */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 mb-3 sm:mb-4">
                <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                    {/* Search */}
                    <div className="relative">
                        <input
                            type="text"
                            placeholder="Nome ou categoria do produto"
                            value={searchTerm}
                            onChange={(e) => onSearchChange(e.target.value)}
                            disabled={isDisabled}
                            className={`w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2 text-sm sm:text-base border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${isDisabled ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                        />
                        <Search size={18} className="absolute left-2.5 sm:left-3 top-2.5 sm:top-2.5 text-gray-400 sm:w-[20px] sm:h-[20px]" />
                    </div>

                    {/* Código de Barra */}
                    <div className="relative">
                        <input
                            type="text"
                            placeholder="Código de barra"
                            value={barcode}
                            onChange={(e) => onBarcodeChange(e.target.value)}
                            disabled={isDisabled}
                            className={`w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2 text-sm sm:text-base border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${isDisabled ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                        />
                        <Search size={18} className="absolute left-2.5 sm:left-3 top-2.5 sm:top-2.5 text-gray-400 sm:w-[20px] sm:h-[20px]" />
                    </div>

                    {/* Código Interno */}
                    <div className="relative sm:col-span-2 lg:col-span-1">
                        <input
                            type="text"
                            placeholder="Código interno"
                            value={internalCode}
                            onChange={(e) => onInternalCodeChange(e.target.value)}
                            disabled={isDisabled}
                            className={`w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2 text-sm sm:text-base border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${isDisabled ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`}
                        />
                        <Search size={18} className="absolute left-2.5 sm:left-3 top-2.5 sm:top-2.5 text-gray-400 sm:w-[20px] sm:h-[20px]" />
                    </div>
                </div>

                {/* Botões à direita */}
                <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                    {(loadingProdutos || loadingLinked) && (
                        <div className="flex items-center gap-2 text-blue-600">
                            <RefreshCw size={14} className="animate-spin sm:w-[16px] sm:h-[16px]" />
                        </div>
                    )}
                    {onSearchClick && (
                        <button
                            onClick={onSearchClick}
                            disabled={isDisabled || (!searchTerm.trim() && !barcode.trim() && !internalCode.trim())}
                            className={`h-[38px] sm:h-[42px] px-3 sm:px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center gap-1.5 sm:gap-2 transition-colors text-sm sm:text-base ${isDisabled || (!searchTerm.trim() && !barcode.trim() && !internalCode.trim()) ? 'opacity-50 cursor-not-allowed' : ''
                                }`}
                        >
                            <Search size={14} className="sm:w-[16px] sm:h-[16px]" />
                            <span className="whitespace-nowrap">Buscar</span>
                        </button>
                    )}
                    <button
                        onClick={onToggleAll}
                        disabled={isDisabled}
                        className={`text-gray-600 hover:text-gray-800 flex items-center gap-1.5 sm:gap-2 text-sm sm:text-base ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        <div className={`w-4 h-4 sm:w-5 sm:h-5 border-2 rounded flex items-center justify-center ${allSelected ? 'bg-blue-500 border-blue-500 text-white' : 'border-gray-400'
                            }`}>
                            {allSelected && <Check size={12} className="sm:w-[14px] sm:h-[14px]" />}
                        </div>
                        <span className="whitespace-nowrap">Selecionar todos</span>
                    </button>
                </div>
            </div>

            {/* Segunda linha: Dropdowns de filtro */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
                    {/* Category Filter - Custom Dropdown with Search */}
                    <CategoryDropdown
                        selectedCategory={selectedCategory}
                        categorias={categorias}
                        onCategoryChange={onCategoryChange}
                        loadingProdutos={loadingProdutos}
                        loadingLinked={loadingLinked}
                    />

                    {/* Unit Type Filter - Custom Dropdown */}
                    <UnitTypeDropdown
                        selectedUnitTypes={selectedUnitTypes}
                        onUnitTypeChange={onUnitTypeChange}
                        isDisabled={isDisabled}
                    />

                    {/* Screen Status Filter */}
                    <div className="relative">
                        <select
                            value={screenFilter}
                            onChange={(e) => onScreenFilterChange(e.target.value as 'all' | 'linked' | 'unlinked')}
                            disabled={isDisabled}
                            className={`w-full px-3 sm:px-4 py-2 pr-8 sm:pr-10 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none text-sm sm:text-base ${isDisabled ? 'opacity-50 cursor-not-allowed bg-gray-100' : 'bg-white hover:bg-gray-50'}`}
                        >
                            <option value="all">Com e sem vínculo</option>
                            <option value="linked">Vinculadas a telas</option>
                            <option value="unlinked">Não vinculadas</option>
                        </select>
                        <ChevronDown
                            size={18}
                            className={`absolute right-2.5 sm:right-3 top-1/2 transform -translate-y-1/2 flex-shrink-0 text-gray-400 pointer-events-none sm:w-[20px] sm:h-[20px] ${isDisabled ? 'opacity-50' : ''}`}
                        />
                    </div>

                    {/* Screen Filter - Custom Dropdown with Search */}
                    <ScreenDropdown
                        selectedScreenFilter={selectedScreenFilter}
                        screens={screens}
                        optimizedScreens={optimizedScreens}
                        onSelectedScreenChange={onSelectedScreenChange}
                        isDisabled={isDisabled}
                    />

                    {/* Sort Options */}
                    <div className="relative">
                        <select
                            value={sortOption}
                            onChange={(e) => onSortChange(e.target.value as SortOption)}
                            disabled={isDisabled}
                            className={`w-full px-3 sm:px-4 py-2 pr-8 sm:pr-10 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none text-sm sm:text-base ${isDisabled ? 'opacity-50 cursor-not-allowed bg-gray-100' : 'bg-white hover:bg-gray-50'}`}
                        >
                            <option value="name-asc">Nome (A-Z)</option>
                            <option value="name-desc">Nome (Z-A)</option>
                            <option value="price-asc">Menor preço</option>
                            <option value="price-desc">Maior preço</option>
                        </select>
                        <ChevronDown
                            size={18}
                            className={`absolute right-2.5 sm:right-3 top-1/2 transform -translate-y-1/2 flex-shrink-0 text-gray-400 pointer-events-none sm:w-[20px] sm:h-[20px] ${isDisabled ? 'opacity-50' : ''}`}
                        />
                    </div>
                </div>
                
                {/* Promotion Filter - Checkbox alinhado à direita */}
                <div className="flex-shrink-0">
                    <button
                        type="button"
                        onClick={() => !isDisabled && onPromotionFilterChange(!promotionFilter)}
                        disabled={isDisabled}
                        className={`text-gray-600 hover:text-gray-800 flex items-center gap-1.5 sm:gap-2 text-sm sm:text-base ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        <div className={`w-4 h-4 sm:w-5 sm:h-5 border-2 rounded flex items-center justify-center ${promotionFilter ? 'bg-blue-500 border-blue-500 text-white' : 'border-gray-400'
                            }`}>
                            {promotionFilter && <Check size={12} className="sm:w-[14px] sm:h-[14px]" />}
                        </div>
                        <span className="whitespace-nowrap">Apenas em promoção</span>
                    </button>
                </div>
            </div>
        </div>
    );
};

// Componente de dropdown customizado para categorias com busca
interface CategoryDropdownProps {
    selectedCategory: 'all' | number;
    categorias: Categoria[];
    onCategoryChange: (value: 'all' | number) => void;
    loadingProdutos?: boolean;
    loadingLinked?: boolean;
}

const CategoryDropdown: React.FC<CategoryDropdownProps> = ({
    selectedCategory,
    categorias,
    onCategoryChange,
    loadingProdutos = false,
    loadingLinked = false
}) => {
    const isDisabled = loadingProdutos || loadingLinked;
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);

    // Filtrar categorias baseado no termo de busca
    const filteredCategorias = useMemo(() => {
        if (!searchTerm.trim()) {
            return categorias;
        }
        const term = searchTerm.toLowerCase();
        return categorias.filter(categoria =>
            categoria.secao.toLowerCase().includes(term) ||
            categoria.departamento.toLowerCase().includes(term)
        );
    }, [categorias, searchTerm]);

    // Obter nome da categoria selecionada
    const selectedCategoryName = useMemo(() => {
        if (selectedCategory === 'all') {
            return 'Todas as categorias';
        }
        const categoria = categorias.find(c => c.secaoId === selectedCategory);
        return categoria?.secao || 'Selecione uma categoria';
    }, [selectedCategory, categorias]);

    // Fechar dropdown ao clicar fora
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                setSearchTerm('');
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            // Focar no campo de busca quando abrir
            setTimeout(() => {
                searchInputRef.current?.focus();
            }, 100);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    const handleSelect = (value: 'all' | number) => {
        onCategoryChange(value);
        setIsOpen(false);
        setSearchTerm('');
    };

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                type="button"
                onClick={() => !isDisabled && setIsOpen(!isOpen)}
                disabled={isDisabled}
                className={`w-full px-3 sm:px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 flex items-center justify-between text-sm sm:text-base ${isDisabled ? 'opacity-50 cursor-not-allowed bg-gray-100' : 'bg-white hover:bg-gray-50'
                    }`}
            >
                <span className="truncate text-left flex-1 pr-2">{selectedCategoryName}</span>
                <ChevronDown
                    size={18}
                    className={`flex-shrink-0 text-gray-400 transition-transform sm:w-[20px] sm:h-[20px] ${isOpen ? 'transform rotate-180' : ''}`}
                />
            </button>

            {isOpen && (
                <div className="absolute z-50 left-0 w-[380px] mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-hidden">
                    {/* Campo de busca */}
                    <div className="p-2 border-b border-gray-200">
                        <div className="relative">
                            <Search size={16} className="absolute left-3 top-2.5 text-gray-400" />
                            <input
                                ref={searchInputRef}
                                type="text"
                                placeholder="Buscar categoria..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                onClick={(e) => e.stopPropagation()}
                            />
                            {searchTerm && (
                                <button
                                    onClick={() => setSearchTerm('')}
                                    className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                                >
                                    <X size={16} />
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Lista de categorias */}
                    <div className="max-h-64 overflow-y-auto">
                        {/* Mostrar "Todas as categorias" apenas se não houver termo de busca */}
                        {!searchTerm.trim() && (
                            <button
                                type="button"
                                onClick={() => handleSelect('all')}
                                className={`w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center justify-between ${selectedCategory === 'all' ? 'bg-blue-50 text-blue-600 font-medium' : ''
                                    }`}
                            >
                                <span className="flex-1 min-w-0 pr-2">Todas as categorias</span>
                                {selectedCategory === 'all' && <Check size={16} className="flex-shrink-0 text-blue-600" />}
                            </button>
                        )}
                        {filteredCategorias.length > 0 ? (
                            filteredCategorias.map(categoria => (
                                <button
                                    key={`${categoria.departamentoId}-${categoria.secaoId}`}
                                    type="button"
                                    onClick={() => handleSelect(categoria.secaoId)}
                                    className={`w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center justify-between ${selectedCategory === categoria.secaoId
                                        ? 'bg-blue-50 text-blue-600 font-medium'
                                        : ''
                                        }`}
                                >
                                    <span className="flex-1 min-w-0 pr-2 whitespace-normal break-words">{categoria.secao}</span>
                                    {selectedCategory === categoria.secaoId && (
                                        <Check size={16} className="flex-shrink-0 text-blue-600" />
                                    )}
                                </button>
                            ))
                        ) : (
                            <div className="px-4 py-8 text-center text-gray-500 text-sm">
                                Nenhuma categoria encontrada
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// Componente de dropdown customizado para telas com busca
interface ScreenDropdownProps {
    selectedScreenFilter: number | 'all';
    screens: Screen[];
    optimizedScreens: TelaBannerOtimizada[];
    onSelectedScreenChange: (value: number | 'all') => void;
    isDisabled?: boolean;
}

const ScreenDropdown: React.FC<ScreenDropdownProps> = ({
    selectedScreenFilter,
    screens,
    optimizedScreens,
    onSelectedScreenChange,
    isDisabled = false
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);

    // Combinar e normalizar telas (priorizar optimizedScreens se disponível)
    const normalizedScreens = useMemo(() => {
        if (optimizedScreens && optimizedScreens.length > 0) {
            return optimizedScreens.map(s => ({
                id: s.id,
                name: s.nome
            }));
        }
        return screens.map(s => ({
            id: s.id,
            name: s.name
        }));
    }, [screens, optimizedScreens]);

    // Filtrar telas baseado no termo de busca
    const filteredScreens = useMemo(() => {
        if (!searchTerm.trim()) {
            return normalizedScreens;
        }
        const term = searchTerm.toLowerCase();
        return normalizedScreens.filter(screen =>
            screen.name.toLowerCase().includes(term)
        );
    }, [normalizedScreens, searchTerm]);

    // Obter nome da tela selecionada
    const selectedScreenName = useMemo(() => {
        if (selectedScreenFilter === 'all') {
            return 'Todas as telas';
        }
        const screen = normalizedScreens.find(s => s.id === selectedScreenFilter);
        return screen ? screen.name : 'Selecione uma tela';
    }, [selectedScreenFilter, normalizedScreens]);

    // Fechar dropdown ao clicar fora
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                setSearchTerm('');
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            // Focar no campo de busca quando abrir
            setTimeout(() => {
                searchInputRef.current?.focus();
            }, 100);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    const handleSelect = (value: number | 'all') => {
        onSelectedScreenChange(value);
        setIsOpen(false);
        setSearchTerm('');
    };

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                type="button"
                onClick={() => !isDisabled && setIsOpen(!isOpen)}
                disabled={isDisabled}
                className={`w-full px-3 sm:px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 flex items-center justify-between text-sm sm:text-base ${isDisabled ? 'opacity-50 cursor-not-allowed bg-gray-100' : 'bg-white hover:bg-gray-50'
                    }`}
            >
                <span className="truncate text-left flex-1 pr-2">{selectedScreenName}</span>
                <ChevronDown
                    size={18}
                    className={`flex-shrink-0 text-gray-400 transition-transform sm:w-[20px] sm:h-[20px] ${isOpen ? 'transform rotate-180' : ''}`}
                />
            </button>

            {isOpen && (
                <div className="absolute z-50 left-0 w-[380px] mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-hidden">
                    {/* Campo de busca */}
                    <div className="p-2 border-b border-gray-200">
                        <div className="relative">
                            <Search size={16} className="absolute left-3 top-2.5 text-gray-400" />
                            <input
                                ref={searchInputRef}
                                type="text"
                                placeholder="Buscar tela..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                onClick={(e) => e.stopPropagation()}
                            />
                            {searchTerm && (
                                <button
                                    onClick={() => setSearchTerm('')}
                                    className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                                >
                                    <X size={16} />
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Lista de telas */}
                    <div className="max-h-64 overflow-y-auto">
                        {/* Mostrar "Todas as telas" apenas se não houver termo de busca */}
                        {!searchTerm.trim() && (
                            <button
                                type="button"
                                onClick={() => handleSelect('all')}
                                className={`w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center justify-between ${selectedScreenFilter === 'all' ? 'bg-blue-50 text-blue-600 font-medium' : ''
                                    }`}
                            >
                                <span className="flex-1 min-w-0 pr-2">Todas as telas</span>
                                {selectedScreenFilter === 'all' && <Check size={16} className="flex-shrink-0 text-blue-600" />}
                            </button>
                        )}
                        {filteredScreens.length > 0 ? (
                            filteredScreens.map(screen => (
                                <button
                                    key={screen.id}
                                    type="button"
                                    onClick={() => handleSelect(screen.id)}
                                    className={`w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center justify-between ${selectedScreenFilter === screen.id
                                        ? 'bg-blue-50 text-blue-600 font-medium'
                                        : ''
                                        }`}
                                >
                                    <span className="flex-1 min-w-0 pr-2 whitespace-normal break-words">{screen.name}</span>
                                    {selectedScreenFilter === screen.id && (
                                        <Check size={16} className="flex-shrink-0 text-blue-600" />
                                    )}
                                </button>
                            ))
                        ) : (
                            <div className="px-4 py-8 text-center text-gray-500 text-sm">
                                Nenhuma tela encontrada
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// Componente de dropdown customizado para tipo de unidade (seleção múltipla)
interface UnitTypeDropdownProps {
    selectedUnitTypes: string[]; // Array de siglas selecionadas
    onUnitTypeChange: (values: string[]) => void;
    isDisabled?: boolean;
}

const UnitTypeDropdown: React.FC<UnitTypeDropdownProps> = ({
    selectedUnitTypes,
    onUnitTypeChange,
    isDisabled = false
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);

    // Filtrar tipos de unidade baseado no termo de busca
    const filteredUnitTypes = useMemo(() => {
        if (!searchTerm.trim()) {
            return TIPOS_UNIDADE;
        }
        const term = searchTerm.toLowerCase();
        return TIPOS_UNIDADE.filter(tipo =>
            tipo.nome.toLowerCase().includes(term) ||
            tipo.sigla.toLowerCase().includes(term)
        );
    }, [searchTerm]);

    // Obter texto do botão baseado nos tipos selecionados
    const selectedUnitTypeName = useMemo(() => {
        if (selectedUnitTypes.length === 0) {
            return 'Todos os tipos';
        }
        if (selectedUnitTypes.length === 1) {
            const tipo = TIPOS_UNIDADE.find(t => t.sigla === selectedUnitTypes[0]);
            return tipo?.nome || selectedUnitTypes[0];
        }
        return `${selectedUnitTypes.length} tipos selecionados`;
    }, [selectedUnitTypes]);

    // Fechar dropdown ao clicar fora
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                setSearchTerm('');
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            // Focar no campo de busca quando abrir
            setTimeout(() => {
                searchInputRef.current?.focus();
            }, 100);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    const handleToggle = (sigla: string) => {
        const siglaUpper = sigla.toUpperCase(); // Garantir maiúsculo
        const isSelected = selectedUnitTypes.includes(siglaUpper);
        
        if (isSelected) {
            // Remover da seleção
            onUnitTypeChange(selectedUnitTypes.filter(s => s !== siglaUpper));
        } else {
            // Adicionar à seleção
            onUnitTypeChange([...selectedUnitTypes, siglaUpper]);
        }
        // Não fechar o dropdown para permitir múltiplas seleções
    };

    const handleClearAll = () => {
        onUnitTypeChange([]);
        // Não fechar o dropdown
    };

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                type="button"
                onClick={() => !isDisabled && setIsOpen(!isOpen)}
                disabled={isDisabled}
                className={`w-full px-3 sm:px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 flex items-center justify-between text-sm sm:text-base ${isDisabled ? 'opacity-50 cursor-not-allowed bg-gray-100' : 'bg-white hover:bg-gray-50'
                    }`}
            >
                <span className="truncate text-left flex-1 pr-2">{selectedUnitTypeName}</span>
                <ChevronDown
                    size={18}
                    className={`flex-shrink-0 text-gray-400 transition-transform sm:w-[20px] sm:h-[20px] ${isOpen ? 'transform rotate-180' : ''}`}
                />
            </button>

            {isOpen && (
                <div className="absolute z-50 left-0 w-[380px] mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-hidden">
                    {/* Container com busca e limpar seleção lado a lado */}
                    <div className="p-2 border-b border-gray-200 flex gap-2">
                        {/* Campo de busca - 50% */}
                        <div className="relative flex-1">
                            <Search size={16} className="absolute left-3 top-2.5 text-gray-400" />
                            <input
                                ref={searchInputRef}
                                type="text"
                                placeholder="Buscar unidade..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                onClick={(e) => e.stopPropagation()}
                            />
                            {searchTerm && (
                                <button
                                    onClick={() => setSearchTerm('')}
                                    className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                                >
                                    <X size={16} />
                                </button>
                            )}
                        </div>
                        {/* Botão limpar seleção - 50% */}
                        {selectedUnitTypes.length > 0 && (
                            <button
                                type="button"
                                onClick={handleClearAll}
                                className="flex-1 px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm whitespace-nowrap"
                            >
                                Limpar seleção
                            </button>
                        )}
                    </div>

                    {/* Lista de tipos de unidade */}
                    <div className="max-h-64 overflow-y-auto">
                        {filteredUnitTypes.length > 0 ? (
                            filteredUnitTypes.map(tipo => {
                                const isSelected = selectedUnitTypes.includes(tipo.sigla);
                                return (
                                    <button
                                        key={tipo.sigla}
                                        type="button"
                                        onClick={() => handleToggle(tipo.sigla)}
                                        className={`w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center justify-between ${isSelected
                                            ? 'bg-blue-50 text-blue-600 font-medium'
                                            : ''
                                            }`}
                                    >
                                        <span className="flex-1 min-w-0 pr-2 whitespace-normal break-words">{tipo.nome}</span>
                                        <div className={`w-4 h-4 border-2 rounded flex items-center justify-center flex-shrink-0 ${isSelected ? 'bg-blue-500 border-blue-500' : 'border-gray-400'}`}>
                                            {isSelected && <Check size={12} className="text-white" />}
                                        </div>
                                    </button>
                                );
                            })
                        ) : (
                            <div className="px-4 py-8 text-center text-gray-500 text-sm">
                                Nenhum tipo encontrado
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};
